import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useLocation } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebaseConfig';

const parseQuery = (search) => {
  const params = new URLSearchParams(search);
  return {
    mode: params.get('mode'),
    oobCode: params.get('oobCode'),
    apiKey: params.get('apiKey'),
    continueUrl: params.get('continueUrl'),
  };
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, oobCode } = useMemo(() => parseQuery(location.search), [location.search]);

  const [status, setStatus] = useState('verifying'); // verifying | ready | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode || (mode && mode !== 'resetPassword')) {
        setStatus('error');
        setErrorMessage('Invalid or missing password reset code.');
        return;
      }
      try {
        const emailForCode = await verifyPasswordResetCode(auth, oobCode);
        setEmail(emailForCode);
        setStatus('ready');
      } catch (error) {
        console.error('Failed to verify reset code:', error);
        setStatus('error');
        setErrorMessage('The password reset link is invalid or has expired.');
      }
    };

    verifyCode();
  }, [mode, oobCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!oobCode) {
      setErrorMessage('Missing password reset code.');
      setStatus('error');
      return;
    }
    if (!password || password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    try {
      setSubmitting(true);
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (error) {
      console.error('Failed to reset password:', error);
      setErrorMessage(error.message || 'We were unable to reset your password. Please try again.');
      setStatus('ready');
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (status === 'verifying') {
      return (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
          Verifying your reset link...
        </Typography>
      );
    }

    if (status === 'error') {
      return (
        <Stack spacing={2}>
          <Alert severity="error">{errorMessage}</Alert>
          <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
            Return to Login
          </Button>
        </Stack>
      );
    }

    if (status === 'success') {
      return (
        <Stack spacing={2} alignItems="center">
          <Alert severity="success" sx={{ width: '100%' }}>
            Password updated successfully. Redirecting you to the login page...
          </Alert>
          <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
            Go to Login
          </Button>
        </Stack>
      );
    }

    return (
      <Stack component="form" spacing={3} onSubmit={handleSubmit}>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">Account</Typography>
          <Typography variant="body1" fontWeight={600}>{email}</Typography>
        </Stack>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <TextField
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          helperText="Must be at least 8 characters long"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(prev => !prev)} edge="end">
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button type="submit" variant="contained" size="large" disabled={submitting}>
          {submitting ? 'Saving...' : 'Set Password'}
        </Button>
      </Stack>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
          <Stack spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h4" fontWeight={700}>Reset Your Password</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Choose a new password for your Mister Food Safety account.
            </Typography>
          </Stack>
          {renderContent()}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
